import * as i0 from '@angular/core';
import { Input, Component, Injectable, ViewEncapsulation, NgModule } from '@angular/core';
import { animationFadeInOut } from '@engineering11/ui-lib/e11-animations';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

const DEFAULT_LOADER_CONFIG = {
    color: 'e11-bg-skin-primary-accent',
    size: 'md',
    fontSize: '1rem',
    fontColor: 'e11-text-skin-base',
    label: '',
    buttonLoader: false,
    fade: false,
    fullScreen: false,
    active: true,
    hasBackdrop: false,
    backdropStyle: 'dark',
};

/**
 * A component that displays a loading spinner with three dots that animate in a loop.
 */
class E11LoaderDotsComponent {
    constructor() {
        /**
         * The color of the dots. This can be a color class or a hex value.
         * @type {string}
         * @default 'e11-bg-skin-primary-accent'
         */
        this.color = DEFAULT_LOADER_CONFIG.color;
        /**
         * The size of the dots. This can be 'sm', 'md', 'lg', or 'xl'.
         * @type {Size}
         * @default 'md'
         */
        this.size = DEFAULT_LOADER_CONFIG.size;
        /**
         * The font size of the label. This can be a string with a px or rem value.
         * @type {string | undefined}
         * @default '1rem'
         */
        this.fontSize = DEFAULT_LOADER_CONFIG.fontSize;
        /**
         * The font color of the label. This can be a color class or a hex value.
         * @type {string | undefined}
         * @default 'e11-text-skin-base'
         * @example 'e11-text-skin-primary', 'e11-text-white'
         */
        this.fontColor = DEFAULT_LOADER_CONFIG.fontColor;
        /**
         * The label to display below the dots.
         * @type {string}
         * @default ''
         */
        this.label = DEFAULT_LOADER_CONFIG.label;
        /**
         * Whether the loader is displayed as a button loader.
         * @type {boolean}
         * @default false
         */
        this.buttonLoader = DEFAULT_LOADER_CONFIG.buttonLoader;
        /**
         * Whether the loader fades in and out.
         * @type {boolean | undefined}
         * @default false
         */
        this.fade = DEFAULT_LOADER_CONFIG.fade;
        /**
         * Whether the loader is displayed full screen.
         * @type {boolean | undefined}
         * @default false
         */
        this.fullScreen = DEFAULT_LOADER_CONFIG.fullScreen;
        /**
         * Whether the loader is active. "Active" means the loader is seen, on, and displayed. It has a z-index of 99 to stack above the backdrop and other elements.
         * @type {boolean | undefined}
         * @default true
         */
        this.active = DEFAULT_LOADER_CONFIG.active;
        /**
         * Whether the loader has a backdrop.
         * @type {boolean}
         * @default false
         */
        this.hasBackdrop = DEFAULT_LOADER_CONFIG.hasBackdrop;
        /**
         * The style of the backdrop. This can be 'dark' or 'light'.
         * @type {Mode}
         * @default 'dark'
         */
        this.backdropStyle = DEFAULT_LOADER_CONFIG.backdropStyle;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11LoaderDotsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11LoaderDotsComponent, isStandalone: true, selector: "e11-loader-dots", inputs: { color: "color", size: "size", fontSize: "fontSize", fontColor: "fontColor", label: "label", buttonLoader: "buttonLoader", fade: "fade", fullScreen: "fullScreen", active: "active", hasBackdrop: "hasBackdrop", backdropStyle: "backdropStyle" }, ngImport: i0, template: `
    <div
      *ngIf="active"
      class="e11-loading-dots-container e11-z-50"
      [ngClass]="{ 'e11-loader-dots-fullscreen': fullScreen, 'e11-loader-dots-fullscreen-active': active }"
      [@animationFadeInOut]
    >
      <div
        class="e11-loading-dots e11-z-50 e11-w-full e11-text-center e11-mt-4"
        [ngClass]="{ 'button-loader': buttonLoader, 'e11-loader-dots-fade': fade }"
      >
        <div
          class="e11-loading-dots-dot e11-rounded-lg e11-inline-block e11-mr-2 e11-w-3 {{ color }}"
          [ngClass]="{ 'e11-h-3': size === 'sm', 'e11-h-14': size === 'md', 'e11-h-24': size === 'lg', 'e11-h-36 e11-w-8': size === 'xl' }"
        ></div>
        <div
          class="e11-loading-dots-dot e11-rounded-lg e11-inline-block e11-mr-2 e11-w-3 {{ color }}"
          [ngClass]="{ 'e11-h-3': size === 'sm', 'e11-h-14': size === 'md', 'e11-h-24': size === 'lg', 'e11-h-36 e11-w-8': size === 'xl' }"
        ></div>
        <div
          class="e11-loading-dots-dot e11-rounded-lg e11-inline-block e11-mr-2 e11-w-3 {{ color }}"
          [ngClass]="{ 'e11-h-3': size === 'sm', 'e11-h-14': size === 'md', 'e11-h-24': size === 'lg', 'e11-h-36 e11-w-8': size === 'xl' }"
        ></div>
        <h3 class="e11-loading-dots-title e11-relative e11-text-center e11-my-4 {{ fontColor }}" *ngIf="!buttonLoader">{{ label }}</h3>
      </div>
    </div>

    <div
      *ngIf="active && fullScreen && hasBackdrop"
      class="e11-backdrop e11-flex e11-inset-0 e11-fixed e11-z-40 e11-backdrop-blur-sm"
      [ngClass]="{ 'e11-backdrop-active': active, 'e11-bg-black/70': backdropStyle === 'dark', 'e11-bg-white/70': backdropStyle === 'light' }"
      [@animationFadeInOut]
    ></div>
  `, isInline: true, styles: [".e11-loading-dots-container.e11-loader-dots-fullscreen{position:fixed;display:flex;width:0;height:0;inset:-1000px 0 0;align-items:center;justify-content:center}.e11-loading-dots-container.e11-loader-dots-fullscreen.e11-loader-dots-fullscreen-active{top:0;width:100%;height:100%}.e11-loading-dots-container .e11-loader-dots-fade{opacity:0;z-index:-2;transition:opacity 1s ease-in-out 0s,z-index 0s ease-in-out 1.2s}.e11-loading-dots-container .e11-loading-dots .e11-loading-dots-dot{animation:dot-keyframes 1.5s infinite ease-in-out}.e11-loading-dots-container .e11-loading-dots .e11-loading-dots-dot:nth-child(2){animation-delay:.5s}.e11-loading-dots-container .e11-loading-dots .e11-loading-dots-dot:nth-child(3){animation-delay:1s}.e11-loading-dots-container .e11-loading-dots.button-loader .dot{border-radius:4px;height:20px;width:4px}.e11-loading-dots-container .e11-loading-dots .e11-loading-dots-title{animation:slideUp 2s ease-in forwards,fadeIn 1s ease-in forwards}@keyframes dot-keyframes{0%{opacity:.4;transform:scale(1)}50%{opacity:1;transform:scale(1.3)}to{opacity:.4;transform:scale(1)}}@keyframes slideUp{0%{top:3rem}to{top:.5rem}}@keyframes fadeIn{0%{opacity:.1}to{opacity:1}}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], animations: [animationFadeInOut()] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11LoaderDotsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-loader-dots', standalone: true, imports: [CommonModule], animations: [animationFadeInOut()], template: `
    <div
      *ngIf="active"
      class="e11-loading-dots-container e11-z-50"
      [ngClass]="{ 'e11-loader-dots-fullscreen': fullScreen, 'e11-loader-dots-fullscreen-active': active }"
      [@animationFadeInOut]
    >
      <div
        class="e11-loading-dots e11-z-50 e11-w-full e11-text-center e11-mt-4"
        [ngClass]="{ 'button-loader': buttonLoader, 'e11-loader-dots-fade': fade }"
      >
        <div
          class="e11-loading-dots-dot e11-rounded-lg e11-inline-block e11-mr-2 e11-w-3 {{ color }}"
          [ngClass]="{ 'e11-h-3': size === 'sm', 'e11-h-14': size === 'md', 'e11-h-24': size === 'lg', 'e11-h-36 e11-w-8': size === 'xl' }"
        ></div>
        <div
          class="e11-loading-dots-dot e11-rounded-lg e11-inline-block e11-mr-2 e11-w-3 {{ color }}"
          [ngClass]="{ 'e11-h-3': size === 'sm', 'e11-h-14': size === 'md', 'e11-h-24': size === 'lg', 'e11-h-36 e11-w-8': size === 'xl' }"
        ></div>
        <div
          class="e11-loading-dots-dot e11-rounded-lg e11-inline-block e11-mr-2 e11-w-3 {{ color }}"
          [ngClass]="{ 'e11-h-3': size === 'sm', 'e11-h-14': size === 'md', 'e11-h-24': size === 'lg', 'e11-h-36 e11-w-8': size === 'xl' }"
        ></div>
        <h3 class="e11-loading-dots-title e11-relative e11-text-center e11-my-4 {{ fontColor }}" *ngIf="!buttonLoader">{{ label }}</h3>
      </div>
    </div>

    <div
      *ngIf="active && fullScreen && hasBackdrop"
      class="e11-backdrop e11-flex e11-inset-0 e11-fixed e11-z-40 e11-backdrop-blur-sm"
      [ngClass]="{ 'e11-backdrop-active': active, 'e11-bg-black/70': backdropStyle === 'dark', 'e11-bg-white/70': backdropStyle === 'light' }"
      [@animationFadeInOut]
    ></div>
  `, styles: [".e11-loading-dots-container.e11-loader-dots-fullscreen{position:fixed;display:flex;width:0;height:0;inset:-1000px 0 0;align-items:center;justify-content:center}.e11-loading-dots-container.e11-loader-dots-fullscreen.e11-loader-dots-fullscreen-active{top:0;width:100%;height:100%}.e11-loading-dots-container .e11-loader-dots-fade{opacity:0;z-index:-2;transition:opacity 1s ease-in-out 0s,z-index 0s ease-in-out 1.2s}.e11-loading-dots-container .e11-loading-dots .e11-loading-dots-dot{animation:dot-keyframes 1.5s infinite ease-in-out}.e11-loading-dots-container .e11-loading-dots .e11-loading-dots-dot:nth-child(2){animation-delay:.5s}.e11-loading-dots-container .e11-loading-dots .e11-loading-dots-dot:nth-child(3){animation-delay:1s}.e11-loading-dots-container .e11-loading-dots.button-loader .dot{border-radius:4px;height:20px;width:4px}.e11-loading-dots-container .e11-loading-dots .e11-loading-dots-title{animation:slideUp 2s ease-in forwards,fadeIn 1s ease-in forwards}@keyframes dot-keyframes{0%{opacity:.4;transform:scale(1)}50%{opacity:1;transform:scale(1.3)}to{opacity:.4;transform:scale(1)}}@keyframes slideUp{0%{top:3rem}to{top:.5rem}}@keyframes fadeIn{0%{opacity:.1}to{opacity:1}}\n"] }]
        }], ctorParameters: () => [], propDecorators: { color: [{
                type: Input
            }], size: [{
                type: Input
            }], fontSize: [{
                type: Input
            }], fontColor: [{
                type: Input
            }], label: [{
                type: Input
            }], buttonLoader: [{
                type: Input
            }], fade: [{
                type: Input
            }], fullScreen: [{
                type: Input
            }], active: [{
                type: Input
            }], hasBackdrop: [{
                type: Input
            }], backdropStyle: [{
                type: Input
            }] } });

const defaultGlobalLoaderConfig = {
    ...DEFAULT_LOADER_CONFIG,
    hasBackdrop: true,
    backdropStyle: 'light',
    label: 'Processing...',
};
class E11LoaderGlobalService {
    constructor() {
        this.config$ = new BehaviorSubject(null);
    }
    open(config) {
        const configToUse = { ...defaultGlobalLoaderConfig, ...config };
        this.config$.next(configToUse);
    }
    close() {
        this.config$.next(null);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11LoaderGlobalService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11LoaderGlobalService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11LoaderGlobalService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

/**
 * Benefits of global loader:
 * - You can always trigger it regardless of whether the original component is destroyed
 * - It only requires one loader in the DOM - no filling the DOM with unnecessary loader code
 */
class E11LoaderGlobalComponent {
    constructor(loaderService) {
        this.loaderService = loaderService;
        this.config = null;
        this.destroy$ = new Subject();
    }
    ngAfterViewInit() {
        this.loaderService.config$.pipe(takeUntil(this.destroy$)).subscribe((config) => (this.config = config));
    }
    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.complete();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11LoaderGlobalComponent, deps: [{ token: E11LoaderGlobalService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11LoaderGlobalComponent, isStandalone: true, selector: "e11-loader-global", ngImport: i0, template: `
    <ng-container *ngIf="config">
      <e11-loader-dots
        [fullScreen]="true"
        [color]="config.color"
        [size]="config.size"
        [fontSize]="config.fontSize"
        [label]="config.label"
        [buttonLoader]="config.buttonLoader"
        [fade]="config.fade"
        [hasBackdrop]="config.hasBackdrop"
        [backdropStyle]="config.backdropStyle"
        [@animationFadeInOut]
      ></e11-loader-dots>
    </ng-container>
  `, isInline: true, dependencies: [{ kind: "component", type: E11LoaderDotsComponent, selector: "e11-loader-dots", inputs: ["color", "size", "fontSize", "fontColor", "label", "buttonLoader", "fade", "fullScreen", "active", "hasBackdrop", "backdropStyle"] }, { kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], animations: [animationFadeInOut()], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11LoaderGlobalComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'e11-loader-global',
                    standalone: true,
                    imports: [E11LoaderDotsComponent, CommonModule],
                    encapsulation: ViewEncapsulation.None,
                    animations: [animationFadeInOut()],
                    template: `
    <ng-container *ngIf="config">
      <e11-loader-dots
        [fullScreen]="true"
        [color]="config.color"
        [size]="config.size"
        [fontSize]="config.fontSize"
        [label]="config.label"
        [buttonLoader]="config.buttonLoader"
        [fade]="config.fade"
        [hasBackdrop]="config.hasBackdrop"
        [backdropStyle]="config.backdropStyle"
        [@animationFadeInOut]
      ></e11-loader-dots>
    </ng-container>
  `,
                }]
        }], ctorParameters: () => [{ type: E11LoaderGlobalService }] });

class E11LoaderDotsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11LoaderDotsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11LoaderDotsModule, imports: [E11LoaderDotsComponent, E11LoaderGlobalComponent], exports: [E11LoaderDotsComponent, E11LoaderGlobalComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11LoaderDotsModule, imports: [E11LoaderDotsComponent, E11LoaderGlobalComponent] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11LoaderDotsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [E11LoaderDotsComponent, E11LoaderGlobalComponent],
                    exports: [E11LoaderDotsComponent, E11LoaderGlobalComponent],
                    providers: [],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { E11LoaderDotsComponent, E11LoaderDotsModule, E11LoaderGlobalComponent, E11LoaderGlobalService };
